/**
 * The time left, drawn as a string of beads.
 *
 * The same metaphor the child's tablet uses, so a parent and a child point at
 * the same picture. One bead is five minutes; a long day doubles the bead
 * rather than adding more of them, because past a dozen or so the beads on a
 * phone shrink into dots nobody can count.
 *
 * This draws a number it is given and computes nothing about the budget: the
 * remaining time comes from `projectRemaining`, which goes through
 * `@kidshub/policy`. A second piece of budget arithmetic here is exactly what
 * that package exists to prevent.
 */

const BASE_BEAD_SECONDS = 5 * 60;
const MAX_BEADS = 16;

export interface BeadString {
  /** How much one bead stands for, so the label can say so. */
  beadSeconds: number;
  /** One entry per bead, from 0 (empty) to 1 (full). */
  fills: number[];
}

export const toBeadString = (input: {
  remainingSeconds: number;
  dailyTotalSeconds: number;
}): BeadString => {
  const remaining = Math.max(input.remainingSeconds, 0);
  // Granted time can take the remainder past the day's budget; the string
  // grows to hold it rather than drawing more full beads than it has.
  const span = Math.max(input.dailyTotalSeconds, remaining);

  let beadSeconds = BASE_BEAD_SECONDS;
  while (Math.ceil(span / beadSeconds) > MAX_BEADS) {
    beadSeconds *= 2;
  }

  const count = Math.ceil(span / beadSeconds);
  const fills = Array.from({ length: count }, (_, index) =>
    Math.max(0, Math.min(1, (remaining - index * beadSeconds) / beadSeconds)),
  );

  return { beadSeconds, fills };
};
